import type {
  Finding,
  ImmediateAction,
  InsightDashboardData,
  KillChainPhaseInfo,
  LogEntry,
  ReportSummary,
} from './insightDashboardTypes';
import { parseLogLines } from './insightDashboardUtils';

/**
 * Returns today's date as YYYYMMDD string for dynamic mock data.
 * Once real API is integrated, this will no longer be needed.
 */
function todayStr(): string {
  const now = new Date();
  const y = String(now.getFullYear());
  const mo = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');

  return `${y}${mo}${d}`;
}

const TODAY = todayStr();

export const mockReportSummary: ReportSummary = {
  generatedDate: '2026-03-06 15:06:35',
  totalEvents: 205,
  uniqueTCodes: 17,
  tCodeDeviations: 184,
  sensitiveTables: 23,
  automationDetected: false,
  uniqueActors: 1,
  uniqueSessions: 2,
  deviationsDetected: 7,
  securityFindings: 13,
  financialExposure: '$315,000+',
  verdict:
    'ACTIVE BREACH – CONCURRENT SESSION EXPLOITATION WITH KERNEL BACKDOOR & PROCUREMENT FRAUD',
  findingsByRisk: {
    CRITICAL: 5,
    HIGH: 4,
    MEDIUM: 4,
  },
};

export const mockImmediateActions: ImmediateAction[] = [
  {
    id: 1,
    action: 'Lock user account TROBINSON immediately',
    priority: 'critical',
    completed: false,
    category: 'Account Security',
  },
  {
    id: 2,
    action: 'Reverse PO 4500006602 and SO 0000010077 modifications',
    priority: 'critical',
    completed: false,
    category: 'Financial Remediation',
  },
  {
    id: 3,
    action: 'Investigate CX8DEBUG custom transaction code and disable',
    priority: 'critical',
    completed: false,
    category: 'System Integrity',
  },
  {
    id: 4,
    action: 'Audit all Z_DEMO_SINGLE role assignments and revoke',
    priority: 'high',
    completed: false,
    category: 'Account Security',
  },
  {
    id: 5,
    action: 'Review all financial documents modified between 05:18-05:35',
    priority: 'high',
    completed: false,
    category: 'Financial Remediation',
  },
  {
    id: 6,
    action: 'Verify "Aragon Inc" entity legitimacy and freeze payments',
    priority: 'high',
    completed: false,
    category: 'Financial Remediation',
  },
  {
    id: 7,
    action:
      'Conduct full session forensics for additional compromised documents',
    priority: 'medium',
    completed: false,
    category: 'Investigation',
  },
  {
    id: 8,
    action:
      'Escalate to law enforcement and legal counsel for fraud investigation',
    priority: 'medium',
    completed: false,
    category: 'Investigation',
  },
];

export const mockFindings: Finding[] = [
  {
    id: 1,
    title: 'Kernel Backdoor Activation via CX8DEBUG',
    riskScore: 98,
    severity: 'CRITICAL',
    type: 'deviation',
    phase: 'weaponization',
    steps: [
      {
        action: 'Launched RDEBUG',
        detail: 'Failed — authorization denied (Reason=2)',
        timestamp: '05:13:00',
        sessionId: '15047',
      },
      {
        action: 'Launched DEBUG',
        detail: 'Failed — authorization denied (Reason=2), 4 sec later',
        timestamp: '05:13:04',
        sessionId: '15047',
      },
      {
        action: 'Launched CX8DEBUG',
        detail: 'Succeeded — custom backdoor with kernel override flag',
        timestamp: '05:13:24',
        sessionId: '15047',
      },
    ],
    impact: 'Kernel-level system access achieved via custom transaction.',
    recommendation:
      'Investigate CX8DEBUG origin, disable it, audit all system-level access.',
    evidenceLines: [65, 66, 69],
    evidenceLogIds: [
      '00000000000000000064',
      '00000000000000000065',
      '00000000000000000069',
    ],
    status: 'pending',
  },
  {
    id: 2,
    title: '10x Purchase Order Fraud — Value Inflation ($315K)',
    riskScore: 95,
    severity: 'CRITICAL',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Opened PO 4500006602 via ME22N',
        timestamp: '05:18:57',
        sessionId: '15047',
      },
      {
        action: 'Changed EKPO value',
        detail: '$35,000 → $350,000 (10x multiplier)',
        timestamp: '05:19:29',
        sessionId: '15047',
      },
      {
        action: 'Triggered accrual adjustments',
        detail: 'ACESOBJ_ITEM tables updated automatically',
        timestamp: '05:19:29',
        sessionId: '15047',
      },
    ],
    impact: '$315,000 net procurement fraud exposure.',
    recommendation:
      'Reverse PO 4500006602. Confirm approval workflow. Check similar POs.',
    evidenceLines: [106, 107, 128, 129, 130, 131, 132, 133, 134],
    evidenceLogIds: [
      '00000000000000000106',
      '00000000000000000108',
      '00000000000000000130',
      '00000000000000000131',
      '00000000000000000133',
    ],
    status: 'pending',
  },
  {
    id: 3,
    title: '10x Purchase Order Fraud — Quantity Inflation',
    riskScore: 94,
    severity: 'CRITICAL',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Modified EKPO quantity',
        detail: '100 KG → 1,000 KG on PO 4500006602',
        timestamp: '05:19:29',
        sessionId: '15047',
      },
      {
        action: 'Propagated to EKKN & EKET',
        detail: 'Account assignment and scheduling tables updated',
        timestamp: '05:19:29',
        sessionId: '15047',
      },
    ],
    impact: 'Coordinated with value inflation — systematic procurement fraud.',
    recommendation:
      'Reverse quantity changes. Cross-reference inventory records.',
    evidenceLines: [105, 107, 127, 128, 132],
    evidenceLogIds: [
      '00000000000000000105',
      '00000000000000000107',
      '00000000000000000127',
      '00000000000000000128',
      '00000000000000000132',
    ],
    status: 'pending',
  },
  {
    id: 4,
    title: '100x Sales Order Fraud — Quantity Inflation',
    riskScore: 92,
    severity: 'CRITICAL',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Opened SO 0000010077 via VA02',
        timestamp: '05:25:18',
        sessionId: '15047',
      },
      {
        action: 'Changed VBEP quantity',
        detail: '20 KG → 2,000 KG (100x multiplier)',
        timestamp: '05:25:44',
        sessionId: '15047',
      },
    ],
    impact: 'Revenue inflation and unauthorized shipment risk.',
    recommendation:
      'Reverse SO modifications. Investigate shipment and revenue impact.',
    evidenceLines: [185],
    evidenceLogIds: ['00000000000000000185'],
    status: 'pending',
  },
  {
    id: 5,
    title: 'Unauthorized Date Insertion in Financial Document',
    riskScore: 88,
    severity: 'HIGH',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Opened document 1001710490… via FB02',
        timestamp: '05:27:42',
        sessionId: '15047',
      },
      {
        action: 'Inserted posting date in BKPF',
        detail: '00.00.0000 → 30.01.2026',
        timestamp: '05:27:58',
        sessionId: '15047',
      },
    ],
    impact: 'Enables period-end manipulation.',
    recommendation:
      'Review business justification. Verify financial statement integrity.',
    evidenceLines: [119],
    evidenceLogIds: ['00000000000000000119'],
    status: 'pending',
  },
  {
    id: 6,
    title: 'Universal Journal Tampering (ACDOCA)',
    riskScore: 85,
    severity: 'HIGH',
    type: 'data_modification',
    phase: 'anti_forensics',
    steps: [
      {
        action: 'Modified ACDOCA entries for document 1001710490…',
        timestamp: '05:32:41',
        sessionId: '15047',
      },
      {
        action: 'Inserted cryptic text',
        detail: '"ABCEG", "asdad" into journal fields',
        timestamp: '05:32:55',
        sessionId: '15047',
      },
    ],
    impact:
      'Corrupts S/4HANA universal journal — single source of truth for all postings.',
    recommendation: 'Restore original ACDOCA entries. Review access controls.',
    evidenceLines: [122, 123, 124, 125],
    evidenceLogIds: [
      '00000000000000000122',
      '00000000000000000123',
      '00000000000000000124',
      '00000000000000000125',
    ],
    status: 'pending',
  },
  {
    id: 7,
    title: 'Customer Master Data Identity Change',
    riskScore: 83,
    severity: 'HIGH',
    type: 'data_modification',
    phase: 'anti_forensics',
    steps: [
      {
        action: 'Opened customer 0000000696 via BP/XD02',
        timestamp: '05:34:17',
        sessionId: '15047',
      },
      {
        action: 'Changed KNA1 name',
        detail: '"Aragon Ltd" → "Aragon Inc"',
        timestamp: '05:34:17',
        sessionId: '15047',
      },
      {
        action: 'Repeated in second session',
        detail: 'Same change via XD02 in Session 15048',
        timestamp: '05:18:45',
        sessionId: '15048',
      },
    ],
    impact:
      'Potential payment misdirection to fraudulent entity across multiple sessions.',
    recommendation:
      'Verify "Aragon Inc" legitimacy. Freeze payments pending review.',
    evidenceLines: [126],
    evidenceLogIds: ['00000000000000000126', '00000000000000000214'],
    status: 'pending',
  },
  {
    id: 8,
    title: 'Cryptic Text Insertion in Financial Line Items',
    riskScore: 81,
    severity: 'HIGH',
    type: 'data_modification',
    phase: 'anti_forensics',
    steps: [
      {
        action: 'Opened document 1001710490… via FB02',
        timestamp: '05:31:47',
        sessionId: '15047',
      },
      {
        action: 'Set item text to "ABCEG"',
        detail: 'BSEG line item modification',
        timestamp: '05:32:10',
        sessionId: '15047',
      },
      {
        action: 'Set assignment number to "asdad"',
        detail: 'Non-standard value in financial field',
        timestamp: '05:32:22',
        sessionId: '15047',
      },
    ],
    impact: 'Anti-forensic tactic — corrupts audit trail readability.',
    recommendation:
      'Restore original BSEG entries. Flag for forensic preservation.',
    evidenceLines: [120, 121],
    evidenceLogIds: ['00000000000000000120', '00000000000000000121'],
    status: 'pending',
  },
  {
    id: 9,
    title:
      'Cross-Session Backdoor Exploitation — USR02 Password Hash Tampering',
    riskScore: 96,
    severity: 'CRITICAL',
    type: 'privilege_abuse',
    phase: 'exploitation',
    steps: [
      {
        action: 'Session 15047 created CX8DEBUG backdoor',
        detail: 'Kernel override after RDEBUG/DEBUG failures',
        timestamp: '05:13:24',
        sessionId: '15047',
      },
      {
        action: 'Session 15048 logged in concurrently',
        detail: 'Overlapping login while session 1 still active',
        timestamp: '05:15:12',
        sessionId: '15048',
      },
      {
        action: 'Session 15048 launched CX8DEBUG',
        detail: 'Reused backdoor tcode created by session 1 — no auth failure',
        timestamp: '05:16:40',
        sessionId: '15048',
      },
      {
        action: 'Direct USR02 write via SE16',
        detail:
          'Modified password hash for user ADMIN_FIORI — auth check bypassed via kernel flag',
        timestamp: '05:16:55',
        sessionId: '15048',
      },
      {
        action: 'Cleared SM21 system log entries',
        detail: 'Anti-forensic cleanup of backdoor usage traces',
        timestamp: '05:17:20',
        sessionId: '15048',
      },
    ],
    impact:
      'Persistent admin access achieved by modifying password hashes through kernel backdoor. Second session exploited weaponization done in first session.',
    recommendation:
      'Reset ALL admin passwords. Disable CX8DEBUG. Correlate both sessions for full blast radius. Check SM21 log gaps.',
    evidenceLines: [69, 215, 216, 217, 218],
    evidenceLogIds: [
      '00000000000000000069',
      '00000000000000000215',
      '00000000000000000216',
      '00000000000000000217',
      '00000000000000000218',
    ],
    status: 'pending',
  },
  {
    id: 10,
    title: 'Business Partner Address Record Manipulation',
    riskScore: 79,
    severity: 'MEDIUM',
    type: 'data_modification',
    phase: 'anti_forensics',
    steps: [
      {
        action: 'Modified ADRC for BP 0000043985',
        detail: '"Aragon Ltd" → "Aragon Inc"',
        timestamp: '05:34:17',
        sessionId: '15047',
      },
      {
        action: 'Modified ADRC for BP 0000043986',
        detail: '"Aragon Ltd" → "Aragon Inc"',
        timestamp: '05:34:17',
        sessionId: '15047',
      },
      {
        action: 'Extended to BP 0000043987 in Session 15048',
        detail: 'Same pattern repeated cross-session',
        timestamp: '05:19:30',
        sessionId: '15048',
      },
    ],
    impact:
      'Coordinated identity changes across sessions support payment fraud hypothesis.',
    recommendation:
      'Verify name changes with stakeholders. Cross-ref payment routing.',
    evidenceLines: [110, 111, 127],
    evidenceLogIds: [
      '00000000000000000109',
      '00000000000000000110',
      '00000000000000000220',
    ],
    status: 'pending',
  },
  {
    id: 11,
    title: 'G/L Account Description Normalization',
    riskScore: 76,
    severity: 'MEDIUM',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Opened G/L YCOA0051100003 via FS00',
        timestamp: '05:18:08',
        sessionId: '15047',
      },
      {
        action: 'Changed SKAT description',
        detail:
          '"Special Project Expense MISC 03" → "Special Project Expense MISC"',
        timestamp: '05:18:22',
        sessionId: '15047',
      },
    ],
    impact: 'Removes specificity — easier to hide fraudulent postings.',
    recommendation:
      'Restore original description. Review postings to this account.',
    evidenceLines: [165],
    evidenceLogIds: ['00000000000000000165'],
    status: 'pending',
  },
  {
    id: 12,
    title: 'Custom Role Creation with Table Display Authorization',
    riskScore: 74,
    severity: 'MEDIUM',
    type: 'privilege_abuse',
    phase: 'reconnaissance',
    steps: [
      {
        action: 'Created role Z_DEMO_SINGLE via PFCG',
        timestamp: '05:02:27',
        sessionId: '15047',
      },
      {
        action: 'Added S_TABU_DIS authorization',
        detail: 'Table display access',
        timestamp: '05:03:38',
        sessionId: '15047',
      },
      {
        action: 'Added S_TCODE + S_APPL_LOG',
        detail: 'Transaction auth + log access',
        timestamp: '05:03:38',
        sessionId: '15047',
      },
    ],
    impact: 'Privilege escalation framework for data reconnaissance.',
    recommendation: 'Revoke Z_DEMO_SINGLE. Audit all assignments.',
    evidenceLines: [14, 15, 16, 17],
    evidenceLogIds: [
      '00000000000000000139',
      '00000000000000000140',
      '00000000000000000141',
      '00000000000000000142',
      '00000000000000000143',
    ],
    status: 'pending',
  },
  {
    id: 13,
    title: 'New User Account Creation with Table Access',
    riskScore: 72,
    severity: 'MEDIUM',
    type: 'privilege_abuse',
    phase: 'reconnaissance',
    steps: [
      {
        action: 'Created user TROBINSON via SU01',
        detail: '73-second pause before creation',
        timestamp: '04:58:22',
        sessionId: '15047',
      },
      {
        action: 'Modified USR21 + USR04 auth tables',
        timestamp: '04:59:57',
        sessionId: '15047',
      },
      {
        action: 'Bulk assignment via SU10',
        detail: 'SPIDATALA + TROBINSON, within 96 sec of creation',
        timestamp: '05:02:13',
        sessionId: '15047',
      },
    ],
    impact: 'Mule account established for persistent access.',
    recommendation:
      'Lock TROBINSON immediately. Verify business justification.',
    evidenceLines: [6, 7, 8, 9, 10, 11],
    evidenceLogIds: [
      '00000000000000000003',
      '00000000000000000004',
      '00000000000000000005',
      '00000000000000000134',
      '00000000000000000135',
      '00000000000000000136',
      '00000000000000000137',
    ],
    status: 'pending',
  },

  // ── Findings for S-15057 (RPATTEL — suspicious basis admin) ──
  {
    id: 14,
    title: 'Unauthorized Privilege Enumeration via SUIM',
    riskScore: 68,
    severity: 'MEDIUM',
    type: 'rare_pattern',
    phase: 'reconnaissance',
    steps: [
      {
        action: 'Executed SUIM user-by-role report',
        detail: 'Queried all users with SAP_ALL profile',
        timestamp: '05:12:00',
        sessionId: '15057',
      },
      {
        action: 'Exported results to local file',
        detail: '340 user records downloaded',
        timestamp: '05:14:30',
        sessionId: '15057',
      },
      {
        action: 'Checked SU53 auth failures for SPIDATALA',
        detail: 'Investigating failed RDEBUG authorization',
        timestamp: '05:22:00',
        sessionId: '15057',
      },
    ],
    impact:
      'Full privilege map of the system exported — could enable targeted escalation.',
    recommendation:
      'Audit RPATTEL access. Verify business justification for SUIM export.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'pending',
  },

  // ── Findings for S-15058 (DLOPEZ — suspicious vendor changes) ──
  {
    id: 15,
    title: 'Vendor Bank Account Modification During Fraud Window',
    riskScore: 87,
    severity: 'HIGH',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Opened vendor 0000004120 via FK02',
        detail: 'Changed bank routing number',
        timestamp: '05:18:00',
        sessionId: '15058',
      },
      {
        action: 'Modified payment terms',
        detail: 'Net 30 → Immediate payment',
        timestamp: '05:20:45',
        sessionId: '15058',
      },
      {
        action: 'Updated alternative payee',
        detail: 'Added new payee on vendor master',
        timestamp: '05:28:00',
        sessionId: '15058',
      },
    ],
    impact:
      'Vendor payment could be redirected to attacker-controlled account.',
    recommendation:
      'Freeze vendor 0000004120 payments. Verify bank details with vendor.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'pending',
  },
  {
    id: 16,
    title: 'Suspicious Customer Credit Limit Override',
    riskScore: 72,
    severity: 'MEDIUM',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Changed credit limit for customer 0000000696 via FD32',
        detail: '$50K → $500K',
        timestamp: '05:35:00',
        sessionId: '15058',
      },
      {
        action: 'Bypassed credit approval workflow',
        detail: 'Direct master data change, no approval doc',
        timestamp: '05:35:00',
        sessionId: '15058',
      },
    ],
    impact:
      'Allows high-value orders without credit check — potential collusion.',
    recommendation:
      'Review credit limit change. Reinstate original limit pending investigation.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'pending',
  },

  // ── Findings for S-15062 (MTHOMPSON — security admin investigating) ──
  {
    id: 17,
    title: 'Emergency Role Assignment to Investigation Account',
    riskScore: 58,
    severity: 'MEDIUM',
    type: 'privilege_abuse',
    phase: 'weaponization',
    steps: [
      {
        action: 'Assigned SAP_ALL to user ADMIN_SEC via SU01',
        detail: 'Emergency access for incident response',
        timestamp: '07:05:00',
        sessionId: '15062',
      },
      {
        action: 'Granted SE16 debug access',
        detail: 'Table-level read for forensic analysis',
        timestamp: '07:08:30',
        sessionId: '15062',
      },
    ],
    impact:
      'Over-provisioned emergency access — could be exploited if credentials leak.',
    recommendation:
      'Time-box emergency access. Revoke SAP_ALL after investigation.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'reviewed',
  },

  // ── Findings for S-15063 (TROBINSON — exfiltration) ──
  {
    id: 18,
    title: 'Mass Customer Data Export via SE16N',
    riskScore: 94,
    severity: 'CRITICAL',
    type: 'data_modification',
    phase: 'exploitation',
    steps: [
      {
        action: 'Queried KNA1 (customer master) via SE16N',
        detail: 'Selected all 2,847 customer records',
        timestamp: '07:12:00',
        sessionId: '15063',
      },
      {
        action: 'Exported to local spreadsheet',
        detail: 'Full customer names, addresses, bank details',
        timestamp: '07:14:30',
        sessionId: '15063',
      },
      {
        action: 'Queried LFA1 (vendor master)',
        detail: 'Downloaded 1,203 vendor records',
        timestamp: '07:22:00',
        sessionId: '15063',
      },
      {
        action: 'Accessed BSEG (accounting line items)',
        detail: '15,000+ financial line items exported',
        timestamp: '07:35:00',
        sessionId: '15063',
      },
    ],
    impact:
      'Full customer/vendor PII and financial data exfiltrated — regulatory breach.',
    recommendation:
      'Notify DPO. Initiate breach notification process. Preserve evidence.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'escalated',
  },
  {
    id: 19,
    title: 'Sensitive Table Access — Password Hash Dump',
    riskScore: 97,
    severity: 'CRITICAL',
    type: 'privilege_abuse',
    phase: 'exploitation',
    steps: [
      {
        action: 'Accessed USR02 via SE16N with kernel override',
        detail: 'Read all password hashes for 340 users',
        timestamp: '07:45:00',
        sessionId: '15063',
      },
      {
        action: 'Exported USR02 to file via CG3Z',
        detail: 'Binary export to /usr/sap/trans/data/',
        timestamp: '07:48:00',
        sessionId: '15063',
      },
    ],
    impact: 'All user password hashes extracted — offline cracking imminent.',
    recommendation:
      'Force password reset for ALL users. Disable CG3Z. Block file export paths.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'escalated',
  },

  // ── Findings for S-15064 (TROBINSON — trace deletion) ──
  {
    id: 20,
    title: 'System Log Tampering — SM21 Entry Deletion',
    riskScore: 91,
    severity: 'CRITICAL',
    type: 'data_modification',
    phase: 'anti_forensics',
    steps: [
      {
        action: 'Opened SM21 system log',
        detail: 'Filtered for time range 07:00-07:50',
        timestamp: '07:55:00',
        sessionId: '15064',
      },
      {
        action: 'Deleted 47 log entries',
        detail: 'Covered SE16N data export and USR02 access traces',
        timestamp: '07:57:00',
        sessionId: '15064',
      },
      {
        action: 'Cleared ST05 SQL trace',
        detail: 'Removed SQL trace evidence of mass table reads',
        timestamp: '08:02:00',
        sessionId: '15064',
      },
    ],
    impact:
      'Forensic evidence destroyed — SM21 log gaps match exfiltration window.',
    recommendation:
      'Cross-reference security audit log (unchanged). Preserve RSAU_READ_LOG.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'pending',
  },
  {
    id: 21,
    title: 'Backdoor Persistence — Scheduled Job Injection',
    riskScore: 88,
    severity: 'HIGH',
    type: 'privilege_abuse',
    phase: 'anti_forensics',
    steps: [
      {
        action: 'Created background job via SM36',
        detail: 'Job ZDATA_SYNC scheduled daily at 02:00',
        timestamp: '08:10:00',
        sessionId: '15064',
      },
      {
        action: 'Job executes program ZEXPORT_MASTER',
        detail: 'Custom ABAP exfiltrating KNA1/LFA1 to FTP nightly',
        timestamp: '08:12:00',
        sessionId: '15064',
      },
    ],
    impact:
      'Persistent data leak — even after account lock, scheduled job continues.',
    recommendation:
      'Delete job ZDATA_SYNC. Remove program ZEXPORT_MASTER. Audit SM37 for other suspicious jobs.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'pending',
  },

  // ── Findings for S-15059 (LNGUYEN — discovered PO anomaly) ──
  {
    id: 22,
    title: 'PO 4500006602 Invoice Mismatch Detected',
    riskScore: 45,
    severity: 'MEDIUM',
    type: 'unusual_combination',
    phase: 'exploitation',
    steps: [
      {
        action: 'Ran MIRO invoice verification for PO 4500006602',
        timestamp: '06:15:00',
        sessionId: '15059',
      },
      {
        action: 'System flagged 3-way match failure',
        detail: 'PO value $350K vs GR value $35K — 10x discrepancy',
        timestamp: '06:16:30',
        sessionId: '15059',
      },
    ],
    impact: 'Invoice verification caught the PO fraud — controls working.',
    recommendation: 'Escalate to procurement manager. Link to Finding #2.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'reviewed',
  },

  // ── Findings for S-15060 (JSMITH — discovered SO anomaly) ──
  {
    id: 23,
    title: 'SO 0000010077 Quantity Alert Triggered',
    riskScore: 42,
    severity: 'MEDIUM',
    type: 'unusual_combination',
    phase: 'exploitation',
    steps: [
      {
        action: 'Reviewed SO 0000010077 via VA03',
        timestamp: '06:30:00',
        sessionId: '15060',
      },
      {
        action: 'Noticed 2,000 KG quantity vs typical 20 KG',
        detail: 'Flagged to supervisor via email',
        timestamp: '06:32:00',
        sessionId: '15060',
      },
    ],
    impact: 'Sales ops detected SO fraud — manual controls effective.',
    recommendation: 'Link to Finding #4. Confirm reversal processed.',
    evidenceLines: [],
    evidenceLogIds: [],
    status: 'reviewed',
  },
];

export const mockKillChainTimeline: KillChainPhaseInfo[] = [
  {
    phase: 'reconnaissance',
    label: 'Reconnaissance',
    timeRange: '04:58:16 – 05:05:42',
    summary:
      'User creation, role assignment, custom role with table access, TSTC table mapping via SE16/SE16N',
  },
  {
    phase: 'weaponization',
    label: 'Weaponization',
    timeRange: '05:13:00 – 05:13:24',
    summary:
      'Failed RDEBUG/DEBUG → successful CX8DEBUG kernel backdoor activation',
  },
  {
    phase: 'exploitation',
    label: 'Exploitation',
    timeRange: '05:18:08 – 05:34:17',
    summary:
      'G/L normalization, PO fraud ($315K), SO fraud (100x), financial doc tampering, master data changes',
  },
  {
    phase: 'anti_forensics',
    label: 'Anti-Forensics',
    timeRange: '05:32:41 – 05:38:06',
    summary:
      'Cryptic text insertion, identity record changes, blending with routine operations',
  },
];

const mockRawLogText = `LOGID|Request ID|Session ID|FF ID|Log Type|Start Time UTC|End Time UTC|User Name|End Point|End Point Key|Account Key|FF Owner|FF Controller|Requested Tcode|Host Name|Severity|Audit Class|Time Zone|Tcode Deviation|Transaction Code|Instance|Report Creation Date & Time|Log Creation Date & Time|Terminal|Program Name|Client|Details
00000000000000000001|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|Dialog Logon|UTC-6|YES|S000|s4hana2021_S4H_21|20260130054006|20260130045816|EC2AMAZ-MJNH8RG|SAPMSYST|100|Logon successful (type=A, method=P)
00000000000000000002|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|S000|s4hana2021_S4H_21|20260130054006|20260130045816|EC2AMAZ-MJNH8RG|SAPMSYST|100|Transaction SESSION_MANAGER started.
00000000000000000003|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130045822|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SU01 started.
00000000000000000004|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Other Events|UTC-6|YES|SU01|s4hana2021_S4H_21|20260130054006|20260130045935|EC2AMAZ-MJNH8RG|SAPMSUU0|100|Dynamic ABAP code: Event -, event type G!, check total -
00000000000000000005|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||High|User Master Changes|UTC-6|YES|SU01|s4hana2021_S4H_21|20260130054006|20260130045957|EC2AMAZ-MJNH8RG|SAPMSSY4|100|User TROBINSON created.
00000000000000000006|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|SU01|s4hana2021_S4H_21|20260130054006|20260130050034|EC2AMAZ-MJNH8RG|SAPMSSY4|100|User master record TROBINSON changed.
00000000000000000007|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|SU01|s4hana2021_S4H_21|20260130054006|20260130050048|EC2AMAZ-MJNH8RG|SAPMSSY4|100|User master record TROBINSON changed.
00000000000000000008|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050133|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SU10 started.
00000000000000000009|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|SU10|s4hana2021_S4H_21|20260130054006|20260130050213|EC2AMAZ-MJNH8RG|SAPMSSY4|100|User master record SPIDATALA changed.
00000000000000000010|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|SU10|s4hana2021_S4H_21|20260130054006|20260130050213|EC2AMAZ-MJNH8RG|SAPMSSY4|100|User master record TROBINSON changed.
00000000000000000011|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Other Events|UTC-6|YES|SU10|s4hana2021_S4H_21|20260130054006|20260130050213|EC2AMAZ-MJNH8RG|SAPMSUU0M|100|Dynamic ABAP code: Event -, event type G!, check total -
00000000000000000012|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050227|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction PFCG started.
00000000000000000013|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|PFCG|s4hana2021_S4H_21|20260130054006|20260130050338|EC2AMAZ-MJNH8RG|SAPLPRGN_TREE|100|Authorization T-SH11044700/S_APPL_LOG created
00000000000000000014|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|PFCG|s4hana2021_S4H_21|20260130054006|20260130050338|EC2AMAZ-MJNH8RG|SAPLPRGN_TREE|100|Authorization T-SH11044700/S_TABU_DIS created
00000000000000000015|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|PFCG|s4hana2021_S4H_21|20260130054006|20260130050338|EC2AMAZ-MJNH8RG|SAPLPRGN_TREE|100|Authorization T-SH11044700/S_TCODE created
00000000000000000016|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Medium|User Master Changes|UTC-6|YES|PFCG|s4hana2021_S4H_21|20260130054006|20260130050338|EC2AMAZ-MJNH8RG|SAPLPRGN_TREE|100|Profile T-SH110447 created
00000000000000000017|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||High|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050511|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Start of transaction Z* failed (Reason=2)
00000000000000000018|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||High|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050515|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Start of transaction Z failed (Reason=2)
00000000000000000019|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050522|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16 started.
00000000000000000020|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130050526|EC2AMAZ-MJNH8RG|SAPLSETB|100|Generic table access call to TSTC with activity 02 (auth. check: passed )
00000000000000000021|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130050527|EC2AMAZ-MJNH8RG|/1BCDWB/DBTSTC|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000022|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050542|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction ZDATALOAD started.
00000000000000000023|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050555|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16 started.
00000000000000000024|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130050557|EC2AMAZ-MJNH8RG|SAPLSETB|100|Generic table access call to TSTC with activity 02 (auth. check: passed )
00000000000000000025|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130050557|EC2AMAZ-MJNH8RG|/1BCDWB/DBTSTC|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000026|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050613|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction Y_TEST_TRANSACTION started.
00000000000000000027|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050720|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SCC4 started.
00000000000000000028|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130050720|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to T000 with activity 03 (auth. check: passed )
00000000000000000029|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050731|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM01 started.
00000000000000000030|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050741|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OB52 started.
00000000000000000031|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050756|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OB52 started.
00000000000000000032|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|OB52|s4hana2021_S4H_21|20260130054006|20260130050800|EC2AMAZ-MJNH8RG|RFOB5210|100|Generic table access call to V_T001B_COFI with activity 02 (auth. check: passed )
00000000000000000033|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050806|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM59 started.
00000000000000000034|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050819|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction RZ11 started.
00000000000000000035|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050831|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction RZ10 started.
00000000000000000036|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050835|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SNOTE started.
00000000000000000037|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050856|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction STMS started.
00000000000000000038|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050906|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OB08 started.
00000000000000000039|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130050906|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to V_TCURR with activity 02 (auth. check: passed )
00000000000000000040|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050919|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OBA3 started.
00000000000000000041|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130050921|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to V_T043G with activity 02 (auth. check: passed )
00000000000000000042|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050928|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OBA4 started.
00000000000000000043|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130050928|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to V_T043T with activity 02 (auth. check: passed )
00000000000000000044|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050943|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SU24 started.
00000000000000000045|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130050956|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction XK01 started.
00000000000000000046|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051024|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction XK02 started.
00000000000000000047|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051032|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FK01 started.
00000000000000000048|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051052|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FK02 started.
00000000000000000049|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051058|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction BP started.
00000000000000000050|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051113|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction XD01 started.
00000000000000000051|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051127|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction XD02 started.
00000000000000000052|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051142|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FD01 started.
00000000000000000053|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051148|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FD02 started.
00000000000000000054|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051159|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FS00 started.
00000000000000000055|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051205|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OBD4 started.
00000000000000000056|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130051205|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to V_T077S with activity 02 (auth. check: passed )
00000000000000000057|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051213|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction F110 started.
00000000000000000058|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051220|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction F111 started.
00000000000000000059|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051229|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM35 started.
00000000000000000060|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051234|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM36 started.
00000000000000000061|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051240|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM37 started.
00000000000000000062|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051250|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE38 started.
00000000000000000063|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051255|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SA38 started.
00000000000000000064|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||High|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051300|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Start of transaction RDEBUG failed (Reason=2)
00000000000000000065|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||High|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051304|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Start of transaction DEBUG failed (Reason=2)
00000000000000000066|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051307|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16 started.
00000000000000000067|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130051310|EC2AMAZ-MJNH8RG|SAPLSETB|100|Generic table access call to TSTC with activity 02 (auth. check: passed )
00000000000000000068|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130051310|EC2AMAZ-MJNH8RG|/1BCDWB/DBTSTC|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000069|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051324|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction CX8DEBUG started.
00000000000000000070|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051336|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16N started.
00000000000000000071|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051336|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000072|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051336|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to TSTCT with activity 03 (auth. check: passed )
00000000000000000073|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051347|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16N started.
00000000000000000074|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051347|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000075|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051347|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to TSTCT with activity 03 (auth. check: passed )
00000000000000000076|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051349|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000077|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051349|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to TSTCT with activity 03 (auth. check: passed )
00000000000000000078|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051356|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM30 started.
00000000000000000079|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130051400|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to V_T077S with activity 03 (auth. check: passed )
00000000000000000080|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051413|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction ME22N started.
00000000000000000081|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051429|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction ME32K started.
00000000000000000082|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051452|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction ME12 started.
00000000000000000083|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051501|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction VA02 started.
00000000000000000084|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051541|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction XK01 started.
00000000000000000085|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051808|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FS00 started.
00000000000000000086|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Other Events|UTC-6|YES|FS00|s4hana2021_S4H_21|20260130054006|20260130051822|EC2AMAZ-MJNH8RG|SAPGL_ACCOUNT_MASTER_START|100|Dynamic ABAP code: Event -, event type G!, check total -
00000000000000000087|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051857|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction ME22N started.
00000000000000000088|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051953|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction VA02 started.
00000000000000000089|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052518|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction VA02 started.
00000000000000000090|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052530|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction VA01 started.
00000000000000000091|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052544|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction VA02 started.
00000000000000000092|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052640|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB02 started.
00000000000000000093|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052720|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction VA02 started.
00000000000000000094|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052742|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB02 started.
00000000000000000095|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052857|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB03 started.
00000000000000000096|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053020|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB02 started.
00000000000000000097|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053147|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB02 started.
00000000000000000098|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053243|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB02 started.
00000000000000000099|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053311|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction BP started.
00000000000000000100|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053449|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction OB08 started.
00000000000000000101|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SM30|s4hana2021_S4H_21|20260130054006|20260130053449|EC2AMAZ-MJNH8RG|SAPMSVMA|100|Generic table access call to V_TCURR with activity 02 (auth. check: passed )
00000000000000000102|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053642|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE38 started.
00000000000000000103|5713|15047|SAVFFID|SECU AUDIT LOG|20260130045448|20260130053813|||||||||Low|Dialog Logon|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053806|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|User Logoff
00000000000000000104|5713|15047|SAVFFID|SYSTEM LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|||s4hana2021_S4H_21|20260130054006|20260130051931|EC2AMAZ-MJNH8RG|RSM13000|100|ADS call retry: SOAP Runtime Exception: CSoapExceptionTransport : HTTP receive
00000000000000000105|5713|15047|SAVFFID|CHANGE LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|YES|ME22N|s4hana2021_S4H_21|20260130054006|20260130051929|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"POAC17101B7397DAC972E112F1AE694EC03EC936E3FCFD3F99991231SCSTACT    0L","Doc.":"0001374739","Table":"ACESOBJ_ITEM","Table Key":"100POAC          17104500006602                      00010-01","Cha.Ind.":"U","Field":"QUANTITY","Short text":"Total Quantity to Accrue","Old val":"                 100 KG","New val":"               1 000 KG"}
00000000000000000106|5713|15047|SAVFFID|CHANGE LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|YES|ME22N|s4hana2021_S4H_21|20260130054006|20260130051929|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"POAC17101B7397DAC972E112F1AE694EC03EC936E3FCFD3F99991231SCSTACT    0L","Doc.":"0001374739","Table":"ACESOBJ_ITEM","Table Key":"100POAC          17104500006602                      00010-01","Cha.Ind.":"U","Field":"TOTAL_ACCR_AMNT_WSL","Short text":"Total Accrual/Deferral Amount in Transaction Currency","Old val":"                      35 000,00 USD","New val":"                     350 000,00 USD"}
00000000000000000107|5713|15047|SAVFFID|CHANGE LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|YES|ME22N|s4hana2021_S4H_21|20260130054006|20260130051929|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"POAC17101B7397DAC972E112F1AE694EC03EC936E3FCFD3F99991231SCSTPLN    0L","Doc.":"0001374740","Table":"ACESOBJ_ITEM","Table Key":"100POAC          17104500006602                      00010-01","Cha.Ind.":"U","Field":"QUANTITY","Short text":"Total Quantity to Accrue","Old val":"                 100 KG","New val":"               1 000 KG"}
00000000000000000108|5713|15047|SAVFFID|CHANGE LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|YES|ME22N|s4hana2021_S4H_21|20260130054006|20260130051929|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"POAC17101B7397DAC972E112F1AE694EC03EC936E3FCFD3F99991231SCSTPLN    0L","Doc.":"0001374740","Table":"ACESOBJ_ITEM","Table Key":"100POAC          17104500006602                      00010-01","Cha.Ind.":"U","Field":"TOTAL_ACCR_AMNT_WSL","Short text":"Total Accrual/Deferral Amount in Transaction Currency","Old val":"                      35 000,00 USD","New val":"                     350 000,00 USD"}
00000000000000000109|5713|15047|SAVFFID|CHANGE LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|YES|BP|s4hana2021_S4H_21|20260130054006|20260130053417|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"BP  0000043985","Doc.":"0001374745","Table":"ADRC","Table Key":"100000004398500010101","Cha.Ind.":"U","Field":"NAME1","Short text":"Name 1","Old val":"Aragon Ltd","New val":"Aragon Inc"}
00000000000000000110|5713|15047|SAVFFID|CHANGE LOG|20260130045448|20260130053813|||||||||02|Dialog Logon|UTC-6|YES|BP|s4hana2021_S4H_21|20260130054006|20260130053417|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"BP  0000043986","Doc.":"0001374746","Table":"ADRC","Table Key":"100000004398600010101","Cha.Ind.":"U","Field":"NAME1","Short text":"Name 1","Old val":"Aragon Ltd","New val":"Aragon Inc"}
00000000000000000201|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Medium|Dialog Logon|UTC-6|YES|S000|s4hana2021_S4H_21|20260130054006|20260130051512|EC2AMAZ-MJNH8RG|SAPMSYST|100|Logon successful (type=A, method=P)
00000000000000000202|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|S000|s4hana2021_S4H_21|20260130054006|20260130051512|EC2AMAZ-MJNH8RG|SAPMSYST|100|Transaction SESSION_MANAGER started.
00000000000000000203|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051545|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16 started.
00000000000000000204|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130051548|EC2AMAZ-MJNH8RG|SAPLSETB|100|Generic table access call to TSTC with activity 03 (auth. check: passed )
00000000000000000205|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051620|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SE16N started.
00000000000000000206|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SE16N|s4hana2021_S4H_21|20260130054006|20260130051622|EC2AMAZ-MJNH8RG|RK_SE16N|100|Generic table access call to USR02 with activity 03 (auth. check: passed )
00000000000000000207|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051715|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SM21 started.
00000000000000000208|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SM21|s4hana2021_S4H_21|20260130054006|20260130051718|EC2AMAZ-MJNH8RG|RSM21000|100|Generic table access call to TSL21 with activity 03 (auth. check: passed )
00000000000000000209|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051800|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction XD02 started.
00000000000000000210|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051830|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction BP started.
00000000000000000211|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052100|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction ME23N started.
00000000000000000212|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130052400|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction FB03 started.
00000000000000000213|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Dialog Logon|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130053000|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|User Logoff
00000000000000000214|5714|15048|SAVFFID|CHANGE LOG|20260130051500|20260130053000|||||||||02|Dialog Logon|UTC-6|YES|XD02|s4hana2021_S4H_21|20260130054006|20260130051845|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"DEBI0000000696","Doc.":"0001374751","Table":"KNA1","Table Key":"1000000000696","Cha.Ind.":"U","Field":"NAME1","Short text":"Name 1","Old val":"Aragon Ltd","New val":"Aragon Inc"}
00000000000000000215|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051640|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction CX8DEBUG started.
00000000000000000216|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||High|User Master Changes|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130051655|EC2AMAZ-MJNH8RG|SAPLSETB|100|Generic table access call to USR02 with activity 02 (auth. check: passed — kernel override)
00000000000000000217|5714|15048|SAVFFID|CHANGE LOG|20260130051500|20260130053000|||||||||02|User Master Changes|UTC-6|YES|SE16|s4hana2021_S4H_21|20260130054006|20260130051658|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"USR020000ADMIN_FIORI","Doc.":"0001374760","Table":"USR02","Table Key":"100ADMIN_FIORI","Cha.Ind.":"U","Field":"BCODE","Short text":"Password Hash","Old val":"$2a$10$xK8q...","New val":"$2a$10$mN3r..."}
00000000000000000218|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||High|Other Events|UTC-6|YES|SM21|s4hana2021_S4H_21|20260130054006|20260130051720|EC2AMAZ-MJNH8RG|RSM21000|100|System log entries deleted for time range 05:13:00-05:17:00 (3 entries removed)
00000000000000000219|5714|15048|SAVFFID|SECU AUDIT LOG|20260130051500|20260130053000|||||||||Low|Transaction Start|UTC-6|YES|SESSION_MANAGER|s4hana2021_S4H_21|20260130054006|20260130051900|EC2AMAZ-MJNH8RG|SAPLSMTR_NAVIGATION|100|Transaction SU01 started.
00000000000000000220|5714|15048|SAVFFID|CHANGE LOG|20260130051500|20260130053000|||||||||02|Dialog Logon|UTC-6|YES|BP|s4hana2021_S4H_21|20260130054006|20260130051930|EC2AMAZ-MJNH8RG|RSM13000|100|{"Obj.":"BP  0000043987","Doc.":"0001374752","Table":"ADRC","Table Key":"100000004398700010101","Cha.Ind.":"U","Field":"NAME1","Short text":"Name 1","Old val":"Aragon Ltd","New val":"Aragon Inc"}`;

// Replace hardcoded date with today's date so mock data is always current-day
const dynamicRawLogText = mockRawLogText.replace(/20260130/g, TODAY);

export const mockLogEntries: LogEntry[] = parseLogLines(dynamicRawLogText);

/* ------------------------------------------------------------------ */
/*  Extended dataset: 20 sessions, 8-hour work day, ~2000+ events      */
/*                                                                     */
/*  Scenario: SAP ERP environment — Jan 30 2026, 04:00–12:00 UTC      */
/*  Mix of normal user activity, suspicious behavior, and fraud        */
/* ------------------------------------------------------------------ */

// Session profiles — each has a behavioral persona
type SessionProfile =
  | 'normal'
  | 'suspicious'
  | 'attacker'
  | 'compromised'
  | 'exfiltration';

interface SessionDef {
  sid: string;
  rid: string;
  start: string;
  end: string;
  user: string;
  profile: SessionProfile;
  eventBias: number; // multiplier for event volume
  highSevBias: number; // 0-1, probability of high/critical events
  tcodeSet: string[]; // which T-codes this session uses
  description: string;
}

function generateExtendedLogEntries(): LogEntry[] {
  // 20 sessions across 8 hours simulating a real attack scenario
  const D = TODAY; // Dynamic date prefix

  const sessions: SessionDef[] = [
    // ── Original attack sessions (preserved) ──
    {
      sid: '15047',
      rid: '5713',
      start: `${D}044800`,
      end: `${D}053813`,
      user: 'SPIDATALA',
      profile: 'attacker',
      eventBias: 1.5,
      highSevBias: 0.12,
      tcodeSet: [
        'SU01',
        'SU10',
        'PFCG',
        'SE16',
        'SE16N',
        'CX8DEBUG',
        'RDEBUG',
        'ME22N',
        'VA02',
        'FB02',
        'FS00',
        'XD02',
        'BP',
      ],
      description: 'Primary attacker — backdoor + procurement fraud',
    },
    {
      sid: '15048',
      rid: '5714',
      start: `${D}051500`,
      end: `${D}053000`,
      user: 'SPIDATALA',
      profile: 'compromised',
      eventBias: 0.6,
      highSevBias: 0.15,
      tcodeSet: ['SE16', 'SE16N', 'SM21', 'XD02', 'BP', 'CX8DEBUG', 'SU01'],
      description: 'Second session — password hash tamper + cleanup',
    },

    // ── Normal business users (morning shift 04:00-08:00) ──
    {
      sid: '15053',
      rid: '5719',
      start: `${D}040500`,
      end: `${D}044500`,
      user: 'MJOHNSON',
      profile: 'normal',
      eventBias: 0.8,
      highSevBias: 0.0,
      tcodeSet: ['ME23N', 'ME21N', 'MIGO', 'MB51', 'MM60'],
      description: 'Procurement clerk — normal PO review',
    },
    {
      sid: '15054',
      rid: '5720',
      start: `${D}041000`,
      end: `${D}050000`,
      user: 'KWILLIAMS',
      profile: 'normal',
      eventBias: 1.0,
      highSevBias: 0.0,
      tcodeSet: ['VA01', 'VA02', 'VA03', 'VL01N', 'VF01'],
      description: 'Sales order processing — routine',
    },
    {
      sid: '15055',
      rid: '5721',
      start: `${D}042000`,
      end: `${D}051500`,
      user: 'AGARCIA',
      profile: 'normal',
      eventBias: 1.2,
      highSevBias: 0.02,
      tcodeSet: ['FB01', 'FB02', 'FB03', 'F110', 'FAGLL03'],
      description: 'Finance — daily posting run',
    },
    {
      sid: '15056',
      rid: '5722',
      start: `${D}043000`,
      end: `${D}052000`,
      user: 'TCHEN',
      profile: 'normal',
      eventBias: 0.7,
      highSevBias: 0.0,
      tcodeSet: ['MM01', 'MM02', 'MM03', 'ME51N', 'ME52N'],
      description: 'Material master maintenance',
    },

    // ── Suspicious sessions overlapping with attacker ──
    {
      sid: '15057',
      rid: '5723',
      start: `${D}050000`,
      end: `${D}060000`,
      user: 'RPATTEL',
      profile: 'suspicious',
      eventBias: 1.3,
      highSevBias: 0.15,
      tcodeSet: ['SE16', 'SE16N', 'SM30', 'SM21', 'SU01', 'SU53', 'SUIM'],
      description: 'Basis admin — privilege checks during attack window',
    },
    {
      sid: '15058',
      rid: '5724',
      start: `${D}051200`,
      end: `${D}055500`,
      user: 'DLOPEZ',
      profile: 'suspicious',
      eventBias: 0.9,
      highSevBias: 0.1,
      tcodeSet: ['FB02', 'FB03', 'FK02', 'XK02', 'FBL1N', 'FBL5N'],
      description: 'Vendor master changes — overlaps with fraud window',
    },

    // ── Post-attack activity (06:00-09:00) ──
    {
      sid: '15059',
      rid: '5725',
      start: `${D}060000`,
      end: `${D}070000`,
      user: 'LNGUYEN',
      profile: 'normal',
      eventBias: 1.1,
      highSevBias: 0.0,
      tcodeSet: ['ME23N', 'MIRO', 'MIR7', 'MR8M', 'MRBR'],
      description: 'Invoice verification — discovers PO anomaly',
    },
    {
      sid: '15060',
      rid: '5726',
      start: `${D}061500`,
      end: `${D}073000`,
      user: 'JSMITH',
      profile: 'normal',
      eventBias: 1.4,
      highSevBias: 0.03,
      tcodeSet: ['VA02', 'VA03', 'VL02N', 'VF02', 'VF03'],
      description: 'Sales ops — flags SO qty discrepancy',
    },
    {
      sid: '15061',
      rid: '5727',
      start: `${D}063000`,
      end: `${D}074500`,
      user: 'BWILSON',
      profile: 'normal',
      eventBias: 0.9,
      highSevBias: 0.0,
      tcodeSet: ['MM02', 'MM03', 'MMBE', 'MD04', 'ME2M'],
      description: 'Inventory review — normal',
    },
    {
      sid: '15062',
      rid: '5728',
      start: `${D}065000`,
      end: `${D}080000`,
      user: 'MTHOMPSON',
      profile: 'suspicious',
      eventBias: 1.0,
      highSevBias: 0.08,
      tcodeSet: ['SU01', 'SU10', 'PFCG', 'SU53', 'SUIM', 'SU21'],
      description: 'Security admin — investigating TROBINSON account',
    },

    // ── Exfiltration attempt (07:00-08:30) ──
    {
      sid: '15063',
      rid: '5729',
      start: `${D}070000`,
      end: `${D}082000`,
      user: 'TROBINSON',
      profile: 'exfiltration',
      eventBias: 1.6,
      highSevBias: 0.1,
      tcodeSet: [
        'SE16',
        'SE16N',
        'SE38',
        'SA38',
        'SM30',
        'LSMW',
        'CG3Z',
        'AL11',
      ],
      description:
        'Mule account — data export attempts using compromised credentials',
    },
    {
      sid: '15064',
      rid: '5730',
      start: `${D}071500`,
      end: `${D}083000`,
      user: 'TROBINSON',
      profile: 'exfiltration',
      eventBias: 1.2,
      highSevBias: 0.08,
      tcodeSet: ['SE16N', 'CG3Z', 'AL11', 'ST05', 'SM21', 'SE38'],
      description: 'Mule account — second session, trace deletion',
    },

    // ── Incident response sessions (08:00-12:00) ──
    {
      sid: '15065',
      rid: '5731',
      start: `${D}080000`,
      end: `${D}100000`,
      user: 'ADMIN_SEC',
      profile: 'normal',
      eventBias: 1.8,
      highSevBias: 0.05,
      tcodeSet: [
        'SM21',
        'SM20',
        'ST22',
        'SM37',
        'SU01',
        'SU10',
        'PFCG',
        'SUIM',
        'SM04',
      ],
      description: 'Security team — lock accounts, revoke roles',
    },
    {
      sid: '15066',
      rid: '5732',
      start: `${D}082000`,
      end: `${D}103000`,
      user: 'ADMIN_BASIS',
      profile: 'normal',
      eventBias: 2.0,
      highSevBias: 0.02,
      tcodeSet: [
        'SM21',
        'SM20',
        'SM51',
        'SM04',
        'SM66',
        'ST05',
        'SE16N',
        'RSAU_READ_LOG',
      ],
      description: 'Basis team — forensic log collection',
    },
    {
      sid: '15067',
      rid: '5733',
      start: `${D}090000`,
      end: `${D}110000`,
      user: 'CFOOFFICE',
      profile: 'normal',
      eventBias: 0.6,
      highSevBias: 0.0,
      tcodeSet: [
        'FB03',
        'FBL1N',
        'FBL3N',
        'FBL5N',
        'FAGLL03',
        'S_ALR_87012078',
      ],
      description: 'CFO office — financial impact review',
    },
    {
      sid: '15068',
      rid: '5734',
      start: `${D}093000`,
      end: `${D}113000`,
      user: 'AUDIT_EXT',
      profile: 'normal',
      eventBias: 0.8,
      highSevBias: 0.0,
      tcodeSet: ['SM21', 'RSAU_READ_LOG', 'SUIM', 'SU53', 'SE16N', 'CDHDR'],
      description: 'External auditor — read-only audit trail review',
    },

    // ── Late morning normal traffic ──
    {
      sid: '15069',
      rid: '5735',
      start: `${D}100000`,
      end: `${D}120000`,
      user: 'PKIM',
      profile: 'normal',
      eventBias: 1.0,
      highSevBias: 0.0,
      tcodeSet: ['ME21N', 'ME22N', 'ME23N', 'MIGO', 'MIRO'],
      description: 'Procurement — new POs (normal operations resume)',
    },
    {
      sid: '15070',
      rid: '5736',
      start: `${D}103000`,
      end: `${D}120000`,
      user: 'HLEE',
      profile: 'normal',
      eventBias: 0.9,
      highSevBias: 0.0,
      tcodeSet: ['VA01', 'VA02', 'VL01N', 'VF01', 'VF02'],
      description: 'Sales — normal order processing',
    },
  ];

  // Severity distributions per profile
  const severityByProfile: Record<SessionProfile, string[]> = {
    normal: ['Low', 'Low', 'Low', 'Low', 'Low', 'Low', 'Low', 'Medium'],
    suspicious: ['Low', 'Low', 'Low', 'Medium', 'Medium', 'Medium', 'High'],
    attacker: ['Low', 'Low', 'Low', 'Medium', 'Medium', 'High', 'Critical'],
    compromised: ['Low', 'Low', 'Medium', 'Medium', 'High', 'High', 'Critical'],
    exfiltration: [
      'Low',
      'Low',
      'Low',
      'Low',
      'Medium',
      'Medium',
      'High',
      'Critical',
    ],
  };

  const auditClassByProfile: Record<SessionProfile, string[]> = {
    normal: [
      'Transaction Start',
      'Transaction Start',
      'Transaction Start',
      'Other Events',
    ],
    suspicious: [
      'Transaction Start',
      'Transaction Start',
      'User Master Changes',
      'Other Events',
      'System Log',
    ],
    attacker: [
      'Transaction Start',
      'User Master Changes',
      'User Master Changes',
      'Other Events',
      'System Log',
    ],
    compromised: [
      'Transaction Start',
      'User Master Changes',
      'Other Events',
      'System Log',
    ],
    exfiltration: [
      'Transaction Start',
      'Transaction Start',
      'Other Events',
      'System Log',
      'User Master Changes',
    ],
  };

  // Detail templates that produce meaningful SAP-like log messages
  const detailsByProfile: Record<
    SessionProfile,
    ((_tc: string, _user: string) => string)[]
  > = {
    normal: [
      (_tc) => `Transaction ${_tc} started.`,
      () =>
        `Generic table access call to TSTC with activity 03 (auth. check: passed )`,
      () => `Dynamic ABAP code: Event -, event type G!, check total -`,
      (_tc) => `Report ${_tc} variant DEFAULT executed`,
    ],
    suspicious: [
      (_tc) => `Transaction ${_tc} started.`,
      () =>
        `Generic table access call to USR02 with activity 03 (auth. check: passed )`,
      (_, user) => `User ${user} checked authorization SU53`,
      (_tc) => `Start of transaction ${_tc} — elevated check triggered`,
      () => `Authorization object S_USER_GRP checked (ACTVT=03, CLASS=*)`,
    ],
    attacker: [
      (_tc) => `Start of transaction ${_tc} failed (Reason=2)`,
      (_tc) => `Transaction ${_tc} started.`,
      (_, user) => `User master record ${user} changed.`,
      () => `Authorization T-SH11044700/S_TCODE created`,
      () =>
        `Generic table access call to USR02 with activity 02 (auth. check: passed — kernel override)`,
      () => `Profile T-SH110447 created`,
    ],
    compromised: [
      (_tc) => `Transaction ${_tc} started.`,
      () =>
        `Generic table access call to USR02 with activity 02 (auth. check: passed — kernel override)`,
      () => `System log entries deleted for time range (3 entries removed)`,
      (_, user) => `Password hash changed for user ${user}`,
      () => `SM21 log gap detected — entries purged`,
    ],
    exfiltration: [
      (_tc) => `Transaction ${_tc} started.`,
      () =>
        `Generic table access call to ${
          ['KNA1', 'LFA1', 'BKPF', 'BSEG', 'EKPO', 'VBAK'][
            Math.floor(Math.random() * 6)
          ]
        } with activity 03 (auth. check: passed )`,
      () => `File export initiated — target: /usr/sap/trans/data/`,
      () => `Batch download: 2,847 records exported to local file`,
      () => `Trace activated on table USR02 — monitoring password fields`,
      (_tc) =>
        `Program ${_tc} executed in background — RFC callback registered`,
    ],
  };

  const terminals: Record<SessionProfile, string[]> = {
    normal: [
      'EC2AMAZ-WS001',
      'EC2AMAZ-WS002',
      'EC2AMAZ-WS003',
      'EC2AMAZ-WS004',
    ],
    suspicious: ['EC2AMAZ-ADMIN1', 'EC2AMAZ-ADMIN2'],
    attacker: ['EC2AMAZ-MJNH8RG'],
    compromised: ['EC2AMAZ-MJNH8RG'],
    exfiltration: ['EC2AMAZ-EXT01', 'EC2AMAZ-MJNH8RG'],
  };

  const entries: LogEntry[] = [];
  let logIdCounter = 300;

  // Deterministic pseudo-random
  let seed = 42;

  const rand = () => {
    seed = (seed * 16807 + 0) % 2147483647;

    return (seed - 1) / 2147483646;
  };

  const pick = <T>(arr: T[]): T => arr[Math.floor(rand() * arr.length)];

  sessions.forEach((session) => {
    const startSec = parseTs(session.start);
    const endSec = parseTs(session.end);
    const duration = endSec - startSec;

    // Base event count scaled by session duration and bias
    const baseCount = Math.max(30, Math.floor((duration / 60) * 1.2));
    const eventCount = Math.floor(baseCount * session.eventBias + rand() * 20);

    const sevOptions = severityByProfile[session.profile];
    const auditOptions = auditClassByProfile[session.profile];
    const detailOptions = detailsByProfile[session.profile];
    const terminalOptions = terminals[session.profile];

    // Create activity bursts — cluster events in 2-4 "busy" windows
    const burstCount = 2 + Math.floor(rand() * 3);
    const bursts: { center: number; spread: number }[] = Array.from(
      { length: burstCount },
      () => ({
        center: 0.15 + rand() * 0.7,
        spread: 0.05 + rand() * 0.1,
      })
    );

    Array.from({ length: eventCount }).forEach(() => {
      // Pick a burst to cluster around, or uniform random
      let t: number;

      if (rand() < 0.7 && bursts.length > 0) {
        const burst = pick(bursts);
        // Normal-ish around burst center
        const offset = ((rand() + rand() + rand() - 1.5) / 1.5) * burst.spread;

        t = Math.max(0.001, Math.min(0.999, burst.center + offset));
      } else {
        t = rand();
      }

      const eventTime = startSec + t * duration;
      const eventTs = formatEpochToTs(eventTime);

      const tcode = pick(session.tcodeSet);
      let sev = pick(sevOptions);

      // Apply high-severity bias
      if (rand() < session.highSevBias && (sev === 'Low' || sev === 'Medium')) {
        sev = rand() < 0.4 ? 'Critical' : 'High';
      }

      const auditClass = pick(auditOptions);
      const detailFn = pick(detailOptions);

      const logId = String(logIdCounter).padStart(20, '0');

      logIdCounter += 1;

      const isHighSev = sev === 'Critical' || sev === 'High';
      const isChangeLog = !isHighSev && rand() > 0.85;
      const logType = isChangeLog ? 'CHANGE LOG' : 'SECU AUDIT LOG';

      entries.push({
        lineNumber: logIdCounter,
        logId,
        requestId: session.rid,
        sessionId: session.sid,
        logType,
        startTimeUtc: session.start,
        endTimeUtc: session.end,
        severity: sev,
        auditClass,
        tcodeDeviation:
          session.profile !== 'normal' || rand() > 0.9 ? 'YES' : 'NO',
        transactionCode: tcode,
        instance: 's4hana2021_S4H_21',
        logCreationDateTime: eventTs,
        terminal: pick(terminalOptions),
        programName: 'SAPLSMTR_NAVIGATION',
        client: '100',
        details: detailFn(tcode, session.user),
      });
    });

    // Logon event
    logIdCounter += 1;
    entries.push({
      lineNumber: logIdCounter,
      logId: String(logIdCounter).padStart(20, '0'),
      requestId: session.rid,
      sessionId: session.sid,
      logType: 'SECU AUDIT LOG',
      startTimeUtc: session.start,
      endTimeUtc: session.end,
      severity: 'Medium',
      auditClass: 'Dialog Logon',
      tcodeDeviation: 'NO',
      transactionCode: 'S000',
      instance: 's4hana2021_S4H_21',
      logCreationDateTime: session.start,
      terminal: pick(terminalOptions),
      programName: 'SAPMSYST',
      client: '100',
      details: `Logon successful (type=A, method=P) user=${session.user}`,
    });

    // Logoff event
    logIdCounter += 1;
    entries.push({
      lineNumber: logIdCounter,
      logId: String(logIdCounter).padStart(20, '0'),
      requestId: session.rid,
      sessionId: session.sid,
      logType: 'SECU AUDIT LOG',
      startTimeUtc: session.start,
      endTimeUtc: session.end,
      severity: 'Low',
      auditClass: 'Dialog Logon',
      tcodeDeviation: 'NO',
      transactionCode: 'SESSION_MANAGER',
      instance: 's4hana2021_S4H_21',
      logCreationDateTime: session.end,
      terminal: pick(terminalOptions),
      programName: 'SAPLSMTR_NAVIGATION',
      client: '100',
      details: `User Logoff user=${session.user}`,
    });
  });

  return entries;
}

function parseTs(ts: string): number {
  const y = parseInt(ts.slice(0, 4), 10);
  const mo = parseInt(ts.slice(4, 6), 10) - 1;
  const d = parseInt(ts.slice(6, 8), 10);
  const h = parseInt(ts.slice(8, 10), 10);
  const m = parseInt(ts.slice(10, 12), 10);
  const s = parseInt(ts.slice(12, 14), 10);

  return new Date(y, mo, d, h, m, s).getTime() / 1000;
}

function formatEpochToTs(epoch: number): string {
  const d = new Date(epoch * 1000);
  const y = String(d.getFullYear());
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');

  return `${y}${mo}${day}${h}${m}${s}`;
}

const extendedLogEntries = generateExtendedLogEntries();

// Merge original real data + extended generated data
const allLogEntries = [...mockLogEntries, ...extendedLogEntries];

export const mockExploitCategories = [
  {
    category: 'Policy Deviation',
    value: 810,
    color: '#8b5cf6',
  },
  {
    category: 'Reconnaissance',
    value: 580,
    color: '#f59e0b',
  },
  {
    category: 'Scope Creep / Policy Violation',
    value: 340,
    color: '#ef4444',
  },
  {
    category: 'Data Manipulation',
    value: 258,
    color: '#0ea5e9',
  },
  {
    category: 'Configuration Tampering',
    value: 97,
    color: '#14b8a6',
  },
  {
    category: 'Financial Exploit',
    value: 92,
    color: '#6366f1',
  },
];

export const mockInsightDashboardData: InsightDashboardData = {
  reportSummary: {
    ...mockReportSummary,
    totalEvents: allLogEntries.length,
    uniqueSessions: 20,
    securityFindings: 23,
    findingsByRisk: {
      CRITICAL: 8,
      HIGH: 6,
      MEDIUM: 9,
    },
  },
  findings: mockFindings,
  immediateActions: mockImmediateActions,
  killChainTimeline: mockKillChainTimeline,
  logEntries: allLogEntries,
  exploitCategories: mockExploitCategories,
};
